// Admin Console GenAI configuration client (PB-1588 domain 2).
//
// Talks to AIS through the auth BFF's `/api/ais/` proxy route: the browser
// holds a session cookie, the BFF exchanges it for the bearer AIS validates.
// AIS additionally requires the `tenant-admin` role on that token, so the
// server remains the authoritative gate.
//
// Field names are snake_case because that is what AIS actually returns — its
// responses are Pydantic models (services/ais/ais/types.py). A hand-written
// camelCase mapping here would be one more thing to keep in step with a
// contract that lives in another language, and it would fail silently when it
// drifted. The wire shape is the honest shape.
//
// This configures AIS. It must never become a second path to a model: nothing
// here names a model or carries a prompt (root CLAUDE.md rule 1).

import { type AdminErrorReader, request } from "./admin-users-api.js";

export type ProviderKey = string;
export type ModelConnectionMode = "chat" | "embedding";
export type ModelConnectionStatus = "active" | "disabled";
export type ModelConnectionTestStatus = "not_tested" | "passed" | "failed";
export type RequirementStatus = "missing" | "disabled" | "active";

/** What AIS needs configured before it can serve a given intent class. */
export interface ModelConnectionRequirement {
  connection_key: string;
  mode: ModelConnectionMode;
  active_connection_id: string | null;
  status: RequirementStatus;
}

export interface ModelConnection {
  id: string;
  connection_key: string;
  provider_key: ProviderKey;
  mode: ModelConnectionMode;
  display_name: string;
  model_or_deployment: string;
  /** A Key Vault REFERENCE, never the credential itself. */
  secret_uri: string;
  endpoint_url: string | null;
  api_version: string | null;
  max_input_tokens: number;
  max_output_tokens: number | null;
  status: ModelConnectionStatus;
  last_test_status: ModelConnectionTestStatus;
  last_tested_at: string | null;
  last_test_error_code: string | null;
  /** Why the last test failed, in AIS's own words. Null when never tested or
   *  the last test passed. */
  last_test_message: string | null;
  activated_at: string | null;
}

export interface TestModelConnectionResult {
  status: "passed" | "failed";
  tested_at: string;
  provider_response_preview: string | null;
  error_code: string | null;
  message: string | null;
}

export interface ActivateModelConnectionResult {
  activated_connection_id: string;
  disabled_connection_id: string | null;
  /** Existing callers keep the previous connection until their cache expires. */
  applies_within_seconds: number;
}

export interface DisableModelConnectionResult {
  disabled_connection_id: string;
  applies_within_seconds: number;
}

export interface CreateModelConnectionInput {
  connection_key: string;
  provider_key: ProviderKey;
  mode: ModelConnectionMode;
  display_name: string;
  model_or_deployment: string;
  secret_uri: string;
  endpoint_url?: string | null;
  api_version?: string | null;
}

const BASE = "/api/ais/v1/admin";

/* The `as` casts below are a compile-time claim about a runtime value that
   crosses two service boundaries (BFF proxy, then AIS). When an envelope key
   is missing — a rename on the AIS side, a proxy that returns an error body
   with a 200 — the cast produces `undefined` where an array is expected, and
   the page renders an empty table that reads as "nothing is configured". For
   an AI-configuration surface that is a materially wrong answer, so check the
   shape the caller depends on and fail loudly instead. */
function expectArray<T>(value: unknown, field: string): T[] {
  if (!Array.isArray(value)) {
    throw new Error(
      `The AI service returned an unexpected response (missing "${field}").`,
    );
  }
  return value as T[];
}

/* The same argument for the WRITE paths, which were casts. A malformed 200 is
   worse here than on a read: `status` absent renders "Test failed: no detail
   returned" for a test the provider may have PASSED, and
   `applies_within_seconds` absent renders "switch over within undefineds". An
   administrator acts on both. Only the fields a caller actually reads are
   required, so an additive server change stays backward compatible. */
function expectFields<T>(
  value: unknown,
  fields: readonly string[],
  action: string,
): T {
  const missing =
    typeof value === "object" && value !== null
      ? fields.filter(
          (field) => (value as Record<string, unknown>)[field] === undefined,
        )
      : fields;
  if (missing.length > 0) {
    throw new Error(
      `The AI service returned an unexpected response to ${action} (missing "${missing.join('", "')}").`,
    );
  }
  return value as T;
}

/* AIS nests its failure fields under `error` (`ais_error_response` in
   `services/ais/ais/errors.py`), where auth puts them at the top level. Reading
   the flat shape made `message` and `code` undefined on EVERY refusal, so the
   admin saw "Request failed (4xx)" instead of the sentence AIS wrote — for
   rules this page's own buttons provoke: endpoint_url and api_version are
   required for Azure OpenAI, secret_uri must be versionless, a connection must
   pass a test before activation, another active connection already exists for
   this key and mode. The BFF forwards the body byte-for-byte, so what arrives
   here is AIS's envelope verbatim (PB-4987).

   `retryable`, `call_id`, `failure_kind` and `details` also travel in that
   envelope; only the two an admin reads are lifted, so nothing here has to
   change when AIS adds another. */
export const readAisErrorEnvelope: AdminErrorReader = (body, status) => {
  const envelope = body as {
    error?: { code?: string; message?: string };
    code?: string;
    message?: string;
  } | null;

  const nested = envelope?.error;
  if (nested) {
    return {
      message: nested.message ?? nested.code ?? `Request failed (${status})`,
      code: nested.code,
    };
  }

  /* FLAT is not a fallback for tidiness — it is the BFF's OWN envelope.
     These routes reach AIS through the auth proxy, and the proxy refuses some
     requests before it contacts AIS at all: no session cookie (401), module not
     enabled (403), no upstream for this path (404), upstream fetch failed
     (502). Auth's exception filter shapes those into `{ code, message }` at the
     top level, so reading only `error` turned "proxy: module is not enabled"
     into "Request failed (403)" and dropped the code entirely — a regression
     this fix introduced, in the class it exists to remove. The 502 matters
     most: it is what an administrator sees when AIS is down, and it said
     strictly less than before.

     Order, not shape-sniffing: nested wins when present, so an AIS refusal is
     still read as AIS's. This teaches the DEFAULT reader nothing — that one
     stays flat-only, which is the opposite direction and is pinned separately. */
  return {
    message:
      envelope?.message ?? envelope?.code ?? `Request failed (${status})`,
    code: envelope?.code,
  };
};

/** Every call in this module is an AIS call, so each one reads AIS's envelope.
 * The shared transport still owns CSRF, credentials and the 204 case. */
export function aisRequest(
  path: string,
  init: RequestInit = {},
): Promise<unknown> {
  return request(path, init, readAisErrorEnvelope);
}

export async function listModelConnectionRequirements(): Promise<
  ModelConnectionRequirement[]
> {
  const response = (await aisRequest(
    `${BASE}/model-connection-requirements`,
  )) as Record<string, unknown>;
  return expectArray<ModelConnectionRequirement>(
    response?.requirements,
    "requirements",
  );
}

export async function listModelConnections(): Promise<ModelConnection[]> {
  const response = (await aisRequest(`${BASE}/model-connections`)) as Record<
    string,
    unknown
  >;
  return expectArray<ModelConnection>(response?.connections, "connections");
}

export async function createModelConnection(
  input: CreateModelConnectionInput,
): Promise<ModelConnection> {
  const response = await aisRequest(`${BASE}/model-connections`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return expectFields<ModelConnection>(
    response,
    ["id"],
    "creating a connection",
  );
}

export async function testModelConnection(
  connectionId: string,
): Promise<TestModelConnectionResult> {
  const response = await aisRequest(
    `${BASE}/model-connections/${encodeURIComponent(connectionId)}/test`,
    { method: "POST" },
  );
  return expectFields<TestModelConnectionResult>(
    response,
    ["status", "tested_at"],
    "testing a connection",
  );
}

/** `replaceActiveConnectionId` names the connection being superseded. AIS
    refuses activation when a connection has not passed a test, so the UI
    should not offer it either — a refusal the user could have predicted is a
    worse experience than a disabled control. */
export async function activateModelConnection(
  connectionId: string,
  replaceActiveConnectionId?: string | null,
): Promise<ActivateModelConnectionResult> {
  const response = await aisRequest(
    `${BASE}/model-connections/${encodeURIComponent(connectionId)}/activate`,
    {
      method: "POST",
      body: JSON.stringify({
        replace_active_connection_id: replaceActiveConnectionId ?? null,
      }),
    },
  );
  return expectFields<ActivateModelConnectionResult>(
    response,
    ["activated_connection_id", "applies_within_seconds"],
    "activating a connection",
  );
}

export async function disableModelConnection(
  connectionId: string,
): Promise<DisableModelConnectionResult> {
  const response = await aisRequest(
    `${BASE}/model-connections/${encodeURIComponent(connectionId)}/disable`,
    { method: "POST" },
  );
  return expectFields<DisableModelConnectionResult>(
    response,
    ["disabled_connection_id", "applies_within_seconds"],
    "disabling a connection",
  );
}

/** One entry in the prompt registry AIS is currently serving. Read-only by
    nature: prompts are reviewed and shipped with the service (ADR-0007), not
    edited by a tenant administrator. */
export interface AisPrompt {
  prompt_id: string;
  /** The document version AIS is serving. An INTEGER on the wire
      (`docs/api/ais.yaml`, `ais/types.py: Field(ge=1)`); it was typed as a
      string here, which is a claim about the server that the server does not
      make. */
  prompt_version: number;
  prompt_checksum: string;
  title: string;
  owner: string;
  /** Which model connection this prompt runs on — the function assignment. */
  connection_key: string;
  has_output_schema: boolean;
  description?: string;
}

export async function listPrompts(): Promise<AisPrompt[]> {
  const response = (await aisRequest(`/api/ais/v1/prompts`)) as Record<
    string,
    unknown
  >;
  return expectArray<AisPrompt>(response?.prompts, "prompts");
}

export interface UsageTotals {
  group: string;
  calls: number;
  failed_calls: number;
  input_tokens: number;
  output_tokens: number;
  p95_latency_ms: number | null;
}

export interface UsageSummary {
  from_ts: string;
  to_ts: string;
  by_module: UsageTotals[];
  by_connection: UsageTotals[];
  totals: UsageTotals;
}

/* Every field the page renders is checked, not only the two breakdowns.
   A malformed 200 — a renamed key, a proxy error body, a null total — used to
   survive the cast and surface as `undefined` inside a cell, which renders as
   a blank figure on a page whose whole job is reporting figures. A blank
   number reads as "zero calls", which is a different and wrong answer. Fail
   here instead, so it becomes the section's error state. */
function expectTotals(value: unknown, field: string): UsageTotals {
  const row = value as Record<string, unknown> | null | undefined;
  const numeric = ["calls", "failed_calls", "input_tokens", "output_tokens"];
  const wellFormed =
    typeof row === "object" &&
    row !== null &&
    typeof row["group"] === "string" &&
    numeric.every((key) => typeof row[key] === "number") &&
    (row["p95_latency_ms"] === null ||
      typeof row["p95_latency_ms"] === "number");
  if (!wellFormed) {
    throw new Error(
      `The AI service returned an unexpected response (bad "${field}").`,
    );
  }
  return row as unknown as UsageTotals;
}

export async function getUsageSummary(days = 30): Promise<UsageSummary> {
  const response = (await aisRequest(
    `/api/ais/v1/admin/usage?days=${encodeURIComponent(String(days))}`,
  )) as Record<string, unknown>;
  // Both breakdowns are checked: a zero-usage tenant returns empty ARRAYS, so
  // an absent key is a broken response, not a quiet period.
  const byModule = expectArray<unknown>(response?.by_module, "by_module");
  const byConnection = expectArray<unknown>(
    response?.by_connection,
    "by_connection",
  );
  if (
    typeof response?.["from_ts"] !== "string" ||
    typeof response?.["to_ts"] !== "string"
  ) {
    throw new Error(
      'The AI service returned an unexpected response (missing "from_ts"/"to_ts").',
    );
  }
  return {
    from_ts: response["from_ts"],
    to_ts: response["to_ts"],
    by_module: byModule.map((row, i) => expectTotals(row, `by_module[${i}]`)),
    by_connection: byConnection.map((row, i) =>
      expectTotals(row, `by_connection[${i}]`),
    ),
    totals: expectTotals(response?.["totals"], "totals"),
  };
}

/* Tenant-scoped for the same reason as the audit keys: the console stays
   mounted across a handover, and model connections, prompts and usage are all
   tenant-bound. */
export const genAiQueryKeys = {
  requirements: (tenantId: string | null | undefined) =>
    ["admin", tenantId ?? "none", "genai", "requirements"] as const,
  connections: (tenantId: string | null | undefined) =>
    ["admin", tenantId ?? "none", "genai", "connections"] as const,
  prompts: (tenantId: string | null | undefined) =>
    ["admin", tenantId ?? "none", "genai", "prompts"] as const,
  usage: (tenantId: string | null | undefined, days: number) =>
    ["admin", tenantId ?? "none", "genai", "usage", days] as const,
};
